export type Permission = 'private' | 'domain' | 'public'

export type Tab = 'mine' | 'shared' | 'starred'

export type ArtifactType = 'html' | 'link'

export interface Artifact {
  id: string
  title: string
  description?: string
  ownerEmail: string
  type: ArtifactType
  linkUrl?: string
  linkOgImageUrl?: string
  sizeBytes: number
  permission: Permission
  recipients: string[]
  viewCount: number
  createdAt: string
  updatedAt: string
  viewUrl: string
  isMine: boolean
  isStarred: boolean
  parentId: string | null
}

export interface Folder {
  id: string
  name: string
  parentId: string | null
  order: number
  createdAt: string
  updatedAt: string
}

export interface CreateArtifactInput {
  title: string
  description?: string
  type: ArtifactType
  html?: string
  linkUrl?: string
  permission: Permission
  recipients?: string[]
  parentId?: string | null
}

interface ApiError {
  error: string
}

async function readJsonError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ApiError
    return body.error ?? `http_${res.status}`
  } catch {
    return `http_${res.status}`
  }
}

export type ArtifactSort = 'updated' | 'created' | 'views' | 'title'

export const SORT_LABEL: Record<ArtifactSort, string> = {
  updated: 'Recently updated',
  created: 'Recently added',
  views: 'Most viewed',
  title: 'Title A–Z',
}

export async function listArtifacts(
  tab: Tab = 'mine',
  parentId?: string | null,
  opts: { q?: string; sort?: ArtifactSort } = {},
): Promise<Artifact[]> {
  const params = new URLSearchParams({ tab })
  // At the root level (parentId === null), don't constrain by parent —
  // the "all artifacts" view should show everything across folders.
  // Inside a specific folder, narrow to that folder.
  if (tab === 'mine' && parentId) {
    params.set('parentId', parentId)
  }
  if (opts.q && opts.q.trim()) params.set('q', opts.q.trim())
  if (opts.sort) params.set('sort', opts.sort)
  const res = await fetch(`/api/artifacts?${params.toString()}`, {
    credentials: 'same-origin',
  })
  if (!res.ok) throw new Error(await readJsonError(res))
  const body = (await res.json()) as { artifacts: Artifact[] }
  return body.artifacts
}

export async function listFolders(): Promise<Folder[]> {
  const res = await fetch('/api/folders', { credentials: 'same-origin' })
  if (!res.ok) throw new Error(await readJsonError(res))
  const body = (await res.json()) as { folders: Folder[] }
  return body.folders
}

export async function createFolder(input: {
  name: string
  parentId?: string | null
}): Promise<Folder> {
  const res = await fetch('/api/folders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(await readJsonError(res))
  return (await res.json()) as Folder
}

export async function updateFolder(
  id: string,
  input: { name?: string; parentId?: string | null },
): Promise<Folder> {
  const res = await fetch(`/api/folders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(await readJsonError(res))
  return (await res.json()) as Folder
}

export async function reorderFolders(
  parentId: string | null,
  orderedIds: string[],
): Promise<void> {
  const res = await fetch('/api/folders/reorder', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ parentId, orderedIds }),
  })
  if (!res.ok && res.status !== 204) throw new Error(await readJsonError(res))
}

export async function deleteFolder(id: string): Promise<void> {
  const res = await fetch(`/api/folders/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) throw new Error(await readJsonError(res))
}

export async function createArtifact(input: CreateArtifactInput): Promise<Artifact> {
  const res = await fetch('/api/artifacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(await readJsonError(res))
  return (await res.json()) as Artifact
}

/** Fetch a single artifact by publicId (e.g. to deep-link its share modal). */
export async function getArtifact(publicId: string): Promise<Artifact> {
  const res = await fetch(`/api/artifacts/${encodeURIComponent(publicId)}`, {
    credentials: 'same-origin',
  })
  if (!res.ok) throw new Error(await readJsonError(res))
  return (await res.json()) as Artifact
}

export interface UpdateArtifactInput {
  title?: string
  description?: string
  permission?: Permission
  recipients?: string[]
  parentId?: string | null
  html?: string
  linkUrl?: string
}

export async function updateArtifact(
  id: string,
  input: UpdateArtifactInput,
): Promise<Artifact> {
  const res = await fetch(`/api/artifacts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  })
  if (!res.ok) throw new Error(await readJsonError(res))
  return (await res.json()) as Artifact
}

export async function deleteArtifact(id: string): Promise<void> {
  const res = await fetch(`/api/artifacts/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) throw new Error(await readJsonError(res))
}

export interface UserSuggestion {
  email: string
  name?: string
  picture?: string
}

export async function suggestUsers(q: string): Promise<UserSuggestion[]> {
  const trimmed = q.trim()
  if (!trimmed) return []
  const res = await fetch(`/api/users/suggest?q=${encodeURIComponent(trimmed)}`, {
    credentials: 'same-origin',
  })
  if (!res.ok) return []
  const body = (await res.json()) as { users: UserSuggestion[] }
  return body.users
}

export async function setStar(id: string, starred: boolean): Promise<void> {
  const res = await fetch(`/api/artifacts/${id}/star`, {
    method: starred ? 'POST' : 'DELETE',
    credentials: 'same-origin',
  })
  if (!res.ok && res.status !== 204) throw new Error(await readJsonError(res))
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const sec = Math.max(0, Math.floor((now - then) / 1000))
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day}d ago`
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export const PERMISSION_LABEL: Record<Permission, string> = {
  private: 'Private',
  domain: 'Leferi · domain',
  public: 'Anyone with link',
}

export const PERMISSION_HELP: Record<Permission, string> = {
  private: 'Only you (and any specific people you add later).',
  domain: 'Anyone signed in with an @leferi.com Google account.',
  public: 'Anyone who has the link, no sign-in required.',
}

export const TAB_LABEL: Record<Tab, string> = {
  mine: 'My artifacts',
  shared: 'Shared with me',
  starred: 'Starred',
}
